/**
 * apps/command/src/components/MaintenanceAndAsset.tsx — §4.8's maintenance
 * windows and §4.7's asset record, on the kiosk detail screen.
 *
 * ============================================================================
 * TWO BLOCKS, TWO CAPABILITIES, AND THE SPLIT IS THE POINT.
 * ============================================================================
 *
 * A MAINTENANCE WINDOW suppresses alerts on a live kiosk — it is the one
 * control in this panel that can make a broken machine look deliberate — so it
 * needs `mbt.command.issue` (governor + operator, D-MBT-04).
 *
 * AN ASSET RECORD is a clipboard. §1.3 gives "asset records" to the technician
 * whom D-MBT-04 withholds remote control from, and refusing them the clipboard
 * while asking them to service the machine would be the wrong way round. So it
 * needs only `mbt.fleet.read`.
 *
 * The duration presets exist because §4.8's window is a planned-work window and
 * planned work is measured in hours. Typing an ISO timestamp to say "I will be
 * an hour" is the kind of friction that makes people skip the window and then
 * wonder why the fleet paged them.
 */
import { useEffect, useState } from 'react';
import type { components } from '@navoiy/api-types';
import { api, ApiError } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { t } from '../i18n/index.js';
import { stamp } from '../lib/format.js';
import { Button, Field, Notice, Panel, PanelHeader, Readout, TextInput } from '../ui/index.jsx';

type MbtKiosk = components['schemas']['MbtKiosk'];

const PRESETS: readonly { key: string; hours: number }[] = [
  { key: 'maintenance.hint1h', hours: 1 },
  { key: 'maintenance.hint4h', hours: 4 },
  { key: 'maintenance.hint24h', hours: 24 },
];

export function MaintenanceAndAsset({
  kiosk,
  onChanged,
  onExpired,
}: {
  kiosk: MbtKiosk;
  onChanged: () => void;
  onExpired: () => void;
}): React.JSX.Element {
  const { allows } = useSession();
  const mayControl = allows('mbt.command.issue');

  const [reason, setReason] = useState('');
  const [hours, setHours] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [editing, setEditing] = useState(false);
  const [asset, setAsset] = useState(kiosk.asset ?? {});
  useEffect(() => {
    // Re-sync when the parent refetches, but NOT while the technician is
    // typing — a poll landing mid-edit that replaced their input with the
    // server's stale copy would lose work with no error to explain it.
    if (!editing) setAsset(kiosk.asset ?? {});
  }, [kiosk.asset, editing]);

  const inWindow =
    kiosk.maintenance_until !== undefined &&
    new Date(kiosk.maintenance_until).getTime() > Date.now();

  async function setWindow(until: string | null, why?: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api().PATCH('/mbt/fleet/kiosks/{device_id}/maintenance', {
        params: { path: { device_id: kiosk.id } },
        body: { until, ...(why ? { reason: why } : {}) },
      });
      setReason('');
      onChanged();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else setError(t('maintenance.failed'));
    } finally {
      setBusy(false);
    }
  }

  async function saveAsset(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api().PATCH('/mbt/fleet/kiosks/{device_id}/asset', {
        params: { path: { device_id: kiosk.id } },
        // Empty string is sent as `null` — a technician clearing a mistyped
        // serial must be able to, and the route distinguishes "cleared" from
        // "not sent" for exactly that reason.
        body: Object.fromEntries(
          Object.entries(asset).map(([key, value]) => [key, value === '' ? null : value]),
        ),
      });
      setEditing(false);
      onChanged();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else setError(t('asset.failed'));
    } finally {
      setBusy(false);
    }
  }

  const field = (key: keyof NonNullable<MbtKiosk['asset']>, label: string): React.JSX.Element =>
    editing ? (
      <Field label={label}>
        <TextInput
          value={(asset[key] as string | null) ?? ''}
          onChange={(event) => setAsset((current) => ({ ...current, [key]: event.target.value }))}
        />
      </Field>
    ) : (
      <Readout
        label={label}
        value={(asset[key] as string | null) ?? null}
        absent={t('asset.empty')}
      />
    );

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Panel>
        <PanelHeader title={t('maintenance.title')} />
        <div className="space-y-3 px-4 py-4">
          {inWindow ? (
            <Notice tone="quiet">
              {t('maintenance.active', {
                until: stamp(new Date(kiosk.maintenance_until as string)),
              })}
              {kiosk.maintenance_reason ? ` · ${kiosk.maintenance_reason}` : ''}
            </Notice>
          ) : (
            <p className="text-label text-ink-500">{t('maintenance.none')}</p>
          )}

          {mayControl ? (
            inWindow ? (
              <Button tone="quiet" onClick={() => void setWindow(null)} disabled={busy}>
                {t('maintenance.close')}
              </Button>
            ) : (
              <>
                <Field label={t('maintenance.reason')} hint={t('maintenance.reasonHint')}>
                  <TextInput
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    maxLength={200}
                  />
                </Field>
                <div>
                  <p className="mb-1 text-label text-ink-300">{t('maintenance.until')}</p>
                  <div className="flex gap-2">
                    {PRESETS.map((preset) => (
                      <Button
                        key={preset.hours}
                        tone={hours === preset.hours ? 'primary' : 'quiet'}
                        onClick={() => setHours(preset.hours)}
                      >
                        {t(preset.key)}
                      </Button>
                    ))}
                  </div>
                  <p className="mt-1 text-micro text-ink-500">{t('maintenance.untilHint')}</p>
                </div>
                <Button
                  onClick={() =>
                    void setWindow(
                      new Date(Date.now() + hours * 3_600_000).toISOString(),
                      reason,
                    )
                  }
                  disabled={busy}
                >
                  {t('maintenance.open')}
                </Button>
              </>
            )
          ) : null}

          {/* Said plainly, because a suppressed kiosk that still counted as
              working would make the governor's figure a lie. */}
          <p className="text-micro text-ink-500">{t('maintenance.note')}</p>
        </div>
      </Panel>

      <Panel>
        <PanelHeader
          title={t('asset.title')}
          aside={
            editing ? undefined : (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-micro text-ink-500 transition-colors duration-state hover:text-ink-300"
              >
                {t('asset.edit')}
              </button>
            )
          }
        />
        <div className="space-y-2 px-4 py-3">
          {field('model', t('asset.model'))}
          {field('serial', t('asset.serial'))}
          {field('install_date', t('asset.installDate'))}
          {field('warranty_until', t('asset.warrantyUntil'))}
          {field('responsible_person', t('asset.responsible'))}
          {field('pinned_version', t('asset.pinnedVersion'))}

          {error ? <Notice tone="crit">{error}</Notice> : null}

          {editing ? (
            <div className="flex gap-3 pt-2">
              <Button onClick={() => void saveAsset()} disabled={busy}>
                {t('asset.save')}
              </Button>
              <Button
                tone="quiet"
                onClick={() => {
                  setEditing(false);
                  setAsset(kiosk.asset ?? {});
                  setError(null);
                }}
              >
                {t('asset.cancel')}
              </Button>
            </div>
          ) : null}
        </div>
      </Panel>
    </div>
  );
}
